import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/Ruckus.css";
import ruckusLogo from "../../assets/images/partners/ruckus.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const wifiGenerations = [
  { std: "Wi-Fi 7", name: "802.11be — Current Gen", speed: "Up to 46 Gbps, Multi-Link Operation, 6 GHz" },
  { std: "Wi-Fi 6E", name: "802.11ax Extended", speed: "Up to 9.6 Gbps, 6 GHz tri-band" },
  { std: "Wi-Fi 6", name: "802.11ax — Mainstream", speed: "Up to 9.6 Gbps, OFDMA, MU-MIMO" },
  { std: "Wi-Fi 5", name: "802.11ac — Legacy Support", speed: "Up to 3.5 Gbps backward compatible" },
];

const campusFeatures = [
  { icon: "📡", title: "BeamFlex+ Adaptive Antenna Technology", desc: "Ruckus patented BeamFlex+ dynamically optimizes signal direction per packet — maintaining reliable connections for mobile users moving through campus environments." },
  { icon: "🌐", title: "SmartZone — Centralized WLAN Management", desc: "SmartZone controller manages thousands of Ruckus APs from a single interface — with automated RF optimization, rogue AP detection, and policy-based client steering." },
  { icon: "🔒", title: "Dynamic Pre-Shared Keys (DPSK)", desc: "Unique per-device encryption keys for BYOD environments — eliminating shared password vulnerabilities without requiring 802.1X certificate infrastructure." },
  { icon: "🏗️", title: "Distributed Antenna System (DAS) Integration", desc: "Ruckus integrates with in-building cellular DAS systems — providing unified wired, wireless, and cellular coverage management for large campus buildings." },
];

const hospItems = [
  { icon: "🛎️", title: "Guest Wi-Fi Portal", desc: "Branded captive portal with PMS integration — auto-provisioning guest network access on check-in, eliminating front desk friction." },
  { icon: "📱", title: "Per-Room Bandwidth Policies", desc: "Granular per-room QoS enforcement ensuring premium suites receive dedicated bandwidth without interference from neighboring rooms." },
  { icon: "🔐", title: "Staff Network Segmentation", desc: "Separate staff, POS, and IoT VLANs isolated from guest traffic with dynamic policy enforcement across all hotel floors." },
  { icon: "📊", title: "Wi-Fi Analytics", desc: "Location analytics tracking guest movement patterns, lobby dwell time, and amenity engagement for operational optimization." },
];

const cityPoints = [
  { num: "01", title: "Outdoor Mesh Networking", desc: "Ruckus outdoor APs and mesh nodes provide city-wide wireless coverage for public parks, transit hubs, and municipal buildings — with weather-rated IP67 enclosures designed for continuous outdoor operation." },
  { num: "02", title: "Public Safety Networks", desc: "FirstNet-compatible wireless infrastructure for police, fire, and EMS communications — with priority QoS for public safety traffic over shared infrastructure." },
  { num: "03", title: "Smart Parking & Transit", desc: "IoT sensor connectivity for parking meters, traffic monitoring cameras, and connected bus stops — all managed through a unified cloud platform with cellular and Wi-Fi backhaul." },
  { num: "04", title: "Digital Equity Initiatives", desc: "Ruckus public Wi-Fi deployments for underserved neighborhoods — with Ruckus Social Wi-Fi enabling authenticated access through existing social credentials or SMS verification." },
];

const iotCols = [
  { icon: "🏥", title: "Healthcare IoT", desc: "Medical device connectivity for infusion pumps, patient monitors, and asset tracking tags — with clinical-grade segmentation keeping IoT devices isolated from EHR systems and patient data." },
  { icon: "🏭", title: "Industrial IoT", desc: "Wireless connectivity for AGVs, RFID readers, barcode scanners, and environmental sensors in manufacturing environments — with deterministic latency for time-sensitive OT applications." },
  { icon: "🏨", title: "Hospitality IoT", desc: "Smart room connectivity for IPTV systems, smart thermostats, door locks, and guest mobile keys — all connected through the same Ruckus wireless infrastructure used for guest internet access." },
];

const cloudFeats = [
  { icon: "☁️", title: "Ruckus One — Unified Cloud Platform", desc: "Ruckus One unifies management of Ruckus APs, ICX switches, and SD-WAN in a single cloud application — replacing multiple management tools with one consolidated interface." },
  { icon: "🤖", title: "AI-Driven RF Optimization", desc: "Ruckus's AI continuously analyzes RF environment, channel utilization, and client density — automatically adjusting transmit power, channel selection, and client band steering." },
  { icon: "🔍", title: "Network Analytics & Troubleshooting", desc: "Ruckus One provides per-client connectivity analytics, latency measurements, and packet loss tracking — enabling root cause analysis without requiring packet capture tools." },
  { icon: "🛡️", title: "Cloud-Native Zero Trust", desc: "Ruckus NAC (Network Access Control) in cloud — dynamic VLAN assignment, IoT device profiling, and policy-based access control managed entirely from the Ruckus One portal." },
];

const deploySteps = [
  { num: "01", title: "RF Survey", desc: "Predictive site survey using your building CAD files and physical walkthrough validation — ensuring optimal AP placement for your specific RF environment, building materials, and client density." },
  { num: "02", title: "Network Design", desc: "Complete WLAN architecture — SSID strategy, VLAN assignment, QoS policies, switch PoE budgeting, and controller topology design aligned to your operational requirements." },
  { num: "03", title: "Zero-Touch Deployment", desc: "Ruckus APs auto-configure from Ruckus One on first boot — eliminating manual per-AP configuration. ALG Data Guard coordinates large multi-AP deployments with minimal technician time per access point." },
  { num: "04", title: "Validation & Handover", desc: "Post-installation RF validation survey confirming coverage, roaming performance, and capacity — with full documentation and training for your IT team on Ruckus One operations." },
];

const industries = [
  { icon: "🎓", name: "Education", desc: "High-density Wi-Fi for lecture halls and student residences, Chromebook/iPad density planning, and network segmentation for student, faculty, and IoT device populations." },
  { icon: "🏟️", name: "Sports & Venues", desc: "Stadium-class dense wireless for 50,000+ concurrent users — Ruckus's installed base includes 200+ professional sports venues and major entertainment arenas." },
  { icon: "🏨", name: "Hospitality", desc: "Hotel and resort Wi-Fi with PMS integration, per-room policies, and branded guest portals — from boutique properties to large convention center hotels." },
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant Wi-Fi with clinical IoT segmentation, location services for asset tracking, and nurse call/RTLS integration on the wireless network." },
  { icon: "🏢", name: "Enterprise Campus", desc: "Large campus switching and wireless fabric with Ruckus ICX and SmartZone — covering headquarters, multi-building campuses, and distributed branch offices." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP-eligible Ruckus cloud management for federal and municipal deployments — with DoD and CJIS security configuration templates." },
  { icon: "🏭", name: "Manufacturing", desc: "Industrial-grade Wi-Fi for factory floors with AGV connectivity, warehouse handheld scanning, and OT device wireless access in challenging RF environments." },
  { icon: "🛒", name: "Retail", desc: "Cloud-managed wireless for retail chains — centrally managed across all locations, with location analytics, guest Wi-Fi marketing integration, and POS device connectivity." },
];

const faqData = [
  { q: "What makes Ruckus Wi-Fi different from Cisco Meraki or Aruba for enterprise deployments?", a: "Ruckus Networks differentiates primarily through its patented BeamFlex+ adaptive antenna technology — a proprietary antenna system that dynamically adjusts signal patterns 1,000 times per second to optimize connectivity for each individual client device. While Meraki and Aruba use conventional omnidirectional antenna designs, Ruckus BeamFlex+ physically steers the signal toward each client rather than broadcasting in all directions equally. This makes Ruckus particularly effective in dense, RF-challenging environments like stadiums, hospitals with thick concrete walls, and manufacturing floors with metal infrastructure. Ruckus has the largest installed base of Wi-Fi in professional sports venues globally — specifically because BeamFlex+ maintains reliable performance under extreme client density. ALG Data Guard recommends Ruckus where RF environment complexity or user density is the primary challenge." },
  { q: "What is BeamFlex+ and why does it matter?", a: "BeamFlex+ is Ruckus's patented software-controlled smart antenna system — a key differentiator built into every Ruckus access point. Traditional Wi-Fi antennas broadcast in fixed patterns (omnidirectional circles). BeamFlex+ uses an array of antenna elements that can be electronically combined in thousands of different patterns. The access point analyzes each incoming packet from each client device and selects the antenna pattern that delivers the strongest signal-to-noise ratio for that specific client at that specific moment. The result is per-packet antenna optimization — adapting to physical obstructions, multipath interference, and client movement in real time. In practical terms, BeamFlex+ delivers measurably better range, throughput, and reliability in challenging RF environments compared to conventional antenna designs — particularly for clients at the cell edge or in obstructed areas." },
  { q: "What is Ruckus One and how does it manage Wi-Fi at scale?", a: "Ruckus One is Ruckus's unified cloud management platform — consolidating management of Ruckus Wi-Fi access points, ICX wired switches, and SD-WAN edges into a single application. Unlike legacy controller-based management requiring separate physical or virtual controllers per site, Ruckus One is purely cloud-native with no on-premises controller infrastructure required. It provides AI-powered RF optimization that automatically adjusts channel selection, transmit power, and band steering across all APs — reducing manual RF tuning work. Ruckus One also includes network analytics, per-client performance monitoring, IoT device profiling, and guest access management. ALG Data Guard implements Ruckus One with appropriate organizational hierarchy (tenant, site, floor level), builds baseline configuration templates, and trains your team on day-to-day operations." },
  { q: "What is the difference between Ruckus SmartZone and Ruckus One?", a: "SmartZone is Ruckus's on-premises or virtual machine-based WLAN controller — a traditional controller architecture where SmartZone software runs in your data center and manages Ruckus APs through a centralized controller. SmartZone is still widely deployed and supported, offering advantages for organizations requiring on-premises management for regulatory, connectivity, or data residency reasons. Ruckus One is the next-generation cloud-native replacement — requiring no on-premises controller infrastructure, with all management through the cloud. New Ruckus deployments typically use Ruckus One for its simplicity and AI features, while existing SmartZone environments can continue operating with SmartZone or migrate to Ruckus One. ALG Data Guard helps organizations evaluate which platform fits their management architecture and compliance requirements." },
  { q: "How does ALG Data Guard support Ruckus environments after deployment?", a: "ALG Data Guard provides comprehensive Ruckus managed services: Ruckus One cloud tenant administration, AP and switch firmware management coordinated within maintenance windows, RF performance monitoring with monthly Wi-Fi health reports including coverage and capacity analysis, guest portal and captive portal administration, policy updates for new SSID requirements or VLAN changes, IoT device profiling updates as new device types are added, zero-trust policy refinement, and annual wireless validation surveys to detect RF environment changes from building renovations or new interference sources. For large multi-site environments, we provide quarterly network health reviews with capacity planning recommendations for technology refresh decisions." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function Ruckus() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(50000, 2200, metricsInView);
  const c2 = useCounter(200, 2000, metricsInView);
  const c3 = useCounter(170, 2000, metricsInView);
  const c4 = useCounter(46, 2000, metricsInView);

  return (
    <div className="rk-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band">
        <div className="pd-identity-inner">
          <img src={ruckusLogo} alt="Ruckus Networks" className="pd-partner-logo" />
          <div className="pd-identity-divider" aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow pd-eyebrow--dark">
              <span className="pd-live-dot pd-live-dot--dark" />
              Enterprise Wi-Fi & Networking Partner
            </div>
            <h1 className="pd-identity-name">ALG Data Guard × Ruckus Networks</h1>
            <p className="pd-identity-desc">
              Ruckus Networks (a CommScope company) is one of the world's most respected enterprise Wi-Fi vendors — known for patented BeamFlex+ adaptive antenna technology that delivers reliable wireless performance in the most challenging RF environments. From stadium Wi-Fi serving 50,000 concurrent users to hospital wireless with clinical IoT segmentation, Ruckus is the standard for enterprise wireless where performance under density truly matters. ALG Data Guard is an authorized Ruckus partner providing design, deployment, and managed wireless services.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="rk-hero">
        <div className="rk-hero__bg" aria-hidden="true" />
        <div className="rk-hero__overlay" aria-hidden="true" />
        <div className="rk-hero__signal" aria-hidden="true">
          <div className="rk-hero__ring rk-hero__ring--1" />
          <div className="rk-hero__ring rk-hero__ring--2" />
          <div className="rk-hero__ring rk-hero__ring--3" />
          <div className="rk-hero__ring rk-hero__ring--4" />
          <div className="rk-hero__ap">📡</div>
        </div>
        <div className="rk-hero__inner">
          <div className="rk-label rk-label--dark"><span className="rk-dot" /> Ruckus Authorized Partner</div>
          <h2 className="rk-headline rk-hero__title">
            Wireless That<br />Works<br /><span className="rk-green-text">Everywhere.</span>
          </h2>
          <p className="rk-hero__body">
            Ruckus Networks delivers enterprise Wi-Fi built for the hardest environments — stadiums, hospitals, manufacturing floors, and high-density campuses where every connection matters. BeamFlex+ adaptive antenna technology optimizes each signal per packet, per client, per environment. ALG Data Guard designs, deploys, and manages Ruckus networks for organizations that can't afford wireless to fail.
          </p>
          <div className="rk-hero__actions">
            <button className="rk-btn rk-btn--green" onClick={() => navigate("/contact")}>
              Design My Wi-Fi Network <ArrowIcon />
            </button>
            <button className="rk-btn rk-btn--ghost-white" onClick={() => navigate("/contact")}>
              Wi-Fi Assessment
            </button>
          </div>
          <div className="rk-hero__specs">
            {[
              { val: "Wi-Fi 7", label: "Latest Generation" },
              { val: "BeamFlex+", label: "Patented Antenna Tech" },
              { val: "200+", label: "Sports Venues" },
              { val: "Partner", label: "ALG Data Guard Tier" },
            ].map(s => (
              <div key={s.val}>
                <div className="rk-hero__spec-val">{s.val}</div>
                <div className="rk-hero__spec-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Enterprise Wi-Fi ── */}
      <section className="rk-wifi">
        <div className="rk-shell">
          <div className="rk-wifi__inner">
            <img
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=900&q=80"
              alt="Ruckus enterprise Wi-Fi"
              className="rk-wifi__img"
            />
            <div>
              <div className="rk-label rk-label--green"><span className="rk-dot" /> Enterprise Wi-Fi</div>
              <h2 className="rk-title">Every Generation. Every Environment.</h2>
              <p className="rk-wifi__body rk-sub" style={{ color:"var(--rk-muted)" }}>
                Ruckus access points span the full Wi-Fi generation spectrum — from Wi-Fi 7 APs for maximum performance environments to Wi-Fi 6 APs for standard enterprise deployments. Every Ruckus AP includes BeamFlex+ adaptive antenna technology and BeamFlex+ ChannelFly dynamic channel selection — improving performance without manual RF tuning.
              </p>
              <p className="rk-wifi__body rk-sub" style={{ color:"var(--rk-muted)" }}>
                ALG Data Guard conducts predictive and validation RF site surveys using iBwave and Ekahau tools — ensuring every Ruckus deployment is engineered for your specific building, not just installed from a standard template.
              </p>
              <div className="rk-wifi__generations">
                {wifiGenerations.map(g => (
                  <div key={g.std} className="rk-gen">
                    <div className="rk-gen__std">{g.std}</div>
                    <div>
                      <div className="rk-gen__name">{g.name}</div>
                      <div className="rk-gen__speed">{g.speed}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="rk-btn rk-btn--green" onClick={() => navigate("/contact")}>
                  Get a Wi-Fi Assessment <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Smart Campus ── */}
      <section className="rk-campus">
        <div className="rk-campus__bg" aria-hidden="true" />
        <div className="rk-campus__overlay" aria-hidden="true" />
        <div className="rk-campus__inner">
          <div>
            <div className="rk-label rk-label--dark"><span className="rk-dot" /> Smart Campuses</div>
            <h2 className="rk-title rk-campus__title">Campus Wireless Built to Last</h2>
            <p className="rk-campus__body">
              Large enterprise campuses — universities, corporate headquarters, healthcare systems — demand wireless infrastructure that self-optimizes, self-heals, and scales without constant engineer intervention. Ruckus SmartZone and Ruckus One deliver campus-wide Wi-Fi management with AI-driven performance optimization that adapts to daily usage patterns automatically.
            </p>
            <div className="rk-campus__features">
              {campusFeatures.map(f => (
                <div key={f.title} className="rk-campus-feat">
                  <span className="rk-campus-feat__icon">{f.icon}</span>
                  <div>
                    <div className="rk-campus-feat__title">{f.title}</div>
                    <p className="rk-campus-feat__desc">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rk-campus__empty" />
        </div>
      </section>

      {/* ── Hospitality ── */}
      <section className="rk-hospitality">
        <div className="rk-shell">
          <div className="rk-hospitality__inner">
            <div>
              <div className="rk-label rk-label--green"><span className="rk-dot" /> Hospitality Wi-Fi</div>
              <h2 className="rk-title rk-hospitality__title">Hotel Wi-Fi Guests Actually Love</h2>
              <p className="rk-hospitality__body rk-sub" style={{ color:"var(--rk-muted)" }}>
                Hotel guests rate Wi-Fi quality as the top factor in their stay experience — ahead of breakfast and price. Ruckus's hospitality portfolio delivers per-room wireless performance that consistently earns 5-star ratings, with integrated management across all hotel systems.
              </p>
              <p className="rk-hospitality__body rk-sub" style={{ color:"var(--rk-muted)" }}>
                ALG Data Guard designs Ruckus hospitality networks integrating with Oracle OPERA, Infor, and Mews PMS systems — enabling automatic guest network provisioning, seamless check-in/check-out network access, and marketing-integrated guest portals.
              </p>
              <div className="rk-hospitality__grid">
                {hospItems.map(item => (
                  <div key={item.title} className="rk-hosp-item">
                    <span className="rk-hosp-item__icon">{item.icon}</span>
                    <div className="rk-hosp-item__title">{item.title}</div>
                    <p className="rk-hosp-item__desc">{item.desc}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="rk-btn rk-btn--green" onClick={() => navigate("/contact")}>
                  Hospitality Wi-Fi Design <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=80"
              alt="Hotel Wi-Fi hospitality"
              className="rk-hospitality__img"
            />
          </div>
        </div>
      </section>

      {/* ── Smart Cities ── */}
      <section className="rk-cities">
        <div className="rk-shell">
          <div className="rk-cities__inner">
            <img
              src="https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=900&q=80"
              alt="Smart city connectivity"
              className="rk-cities__img"
            />
            <div>
              <div className="rk-label rk-label--dark"><span className="rk-dot" /> Smart Cities</div>
              <h2 className="rk-title rk-cities__title">Cities Connected. Citizens Served.</h2>
              <p className="rk-cities__body">
                Ruckus outdoor APs, mesh nodes, and municipal Wi-Fi platforms enable cities and municipalities to deliver public wireless connectivity, support IoT sensor networks, and provide mission-critical connectivity for public safety infrastructure — all through a managed, cloud-connected platform.
              </p>
              <div className="rk-cities__points">
                {cityPoints.map(p => (
                  <div key={p.num} className="rk-city-point">
                    <div className="rk-city-point__num">{p.num}</div>
                    <div>
                      <div className="rk-city-point__title">{p.title}</div>
                      <p className="rk-city-point__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── IoT ── */}
      <section className="rk-iot">
        <div className="rk-shell">
          <div className="rk-iot__big" aria-hidden="true">IoT</div>
          <div className="rk-iot__sub-head">
            <div className="rk-label rk-label--green" style={{ margin:"0 auto 0.8rem" }}><span className="rk-dot" /> IoT Connectivity</div>
            <h2 className="rk-title">One Network. Every Device.</h2>
            <p className="rk-sub" style={{ color:"var(--rk-muted)", maxWidth:"600px", margin:"0 auto 3rem" }}>Ruckus IoT Suite connects Wi-Fi, Bluetooth Low Energy, Zigbee, and Z-Wave IoT devices through the same Ruckus access points — eliminating the need for separate IoT gateway infrastructure.</p>
          </div>
          <div className="rk-iot__cols">
            {iotCols.map(col => (
              <div key={col.title} className="rk-iot-col">
                <span className="rk-iot-col__icon">{col.icon}</span>
                <h3 className="rk-iot-col__title">{col.title}</h3>
                <p className="rk-iot-col__desc">{col.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cloud ── */}
      <section className="rk-cloud">
        <div className="rk-shell">
          <div className="rk-cloud__inner">
            <div>
              <div className="rk-label rk-label--dark"><span className="rk-dot" /> Cloud Management</div>
              <h2 className="rk-title rk-cloud__title">Ruckus One — One Platform. Everything Wireless.</h2>
              <p className="rk-cloud__body">
                Ruckus One is the cloud-native successor to SmartZone — unifying management of Ruckus APs, ICX switches, and SD-WAN in one SaaS application with zero on-premises infrastructure required. AI-powered RF optimization, network analytics, IoT profiling, and zero-trust access control are all included.
              </p>
              <div className="rk-cloud__features">
                {cloudFeats.map(f => (
                  <div key={f.title} className="rk-cloud-feat">
                    <span className="rk-cloud-feat__icon">{f.icon}</span>
                    <div>
                      <div className="rk-cloud-feat__title">{f.title}</div>
                      <p className="rk-cloud-feat__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="rk-btn rk-btn--green" onClick={() => navigate("/contact")}>
                  Explore Ruckus One <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80"
              alt="Ruckus cloud management platform"
              className="rk-cloud__img"
            />
          </div>
        </div>
      </section>

      {/* ── Deployment Journey ── */}
      <section className="rk-deploy">
        <div className="rk-shell">
          <div className="rk-deploy__head">
            <div className="rk-label rk-label--green" style={{ margin:"0 auto 1rem" }}><span className="rk-dot" /> Deployment Journey</div>
            <h2 className="rk-title">From Survey to Signal — Engineered Right.</h2>
            <p className="rk-sub" style={{ color:"var(--rk-muted)", margin:"0 auto", maxWidth:"580px" }}>ALG Data Guard follows a structured engineering methodology for every Ruckus deployment — ensuring your wireless investment performs from day one.</p>
          </div>
          <div className="rk-deploy__steps">
            {deploySteps.map(step => (
              <div key={step.num} className="rk-deploy-step">
                <div className="rk-deploy-step__num">{step.num}</div>
                <h3 className="rk-deploy-step__title">{step.title}</h3>
                <p className="rk-deploy-step__desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="rk-industries">
        <div className="rk-shell">
          <div className="rk-industries__head">
            <div className="rk-label rk-label--green"><span className="rk-dot" /> Industries</div>
            <h2 className="rk-title">Ruckus in Every Vertical</h2>
            <p className="rk-sub" style={{ color:"var(--rk-muted)", margin:"0 auto", maxWidth:"580px" }}>Ruckus delivers validated wireless reference architectures for every industry — with specific configuration templates, compliance documentation, and deployment expertise.</p>
          </div>
          <div className="rk-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="rk-ind-card">
                <span className="rk-ind-card__icon">{ind.icon}</span>
                <h3 className="rk-ind-card__name">{ind.name}</h3>
                <p className="rk-ind-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="rk-metrics" ref={metricsRef}>
        <div className="rk-shell">
          <div className="rk-metrics__inner">
            <div><div className="rk-metric__val">{c1.toLocaleString()}+</div><div className="rk-metric__label">Enterprise Customers</div></div>
            <div><div className="rk-metric__val">{c2}+</div><div className="rk-metric__label">Sports Venues Wi-Fi</div></div>
            <div><div className="rk-metric__val">{c3}+</div><div className="rk-metric__label">Countries Deployed</div></div>
            <div><div className="rk-metric__val">{c4} Gbps</div><div className="rk-metric__label">Wi-Fi 7 Peak Speed</div></div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="rk-faq">
        <div className="rk-shell">
          <div className="rk-faq__head">
            <div className="rk-label rk-label--green" style={{ margin:"0 auto 1rem" }}><span className="rk-dot" /> FAQs</div>
            <h2 className="rk-title">Ruckus Questions, Answered</h2>
            <p className="rk-sub" style={{ color:"var(--rk-muted)", margin:"0 auto", maxWidth:"580px" }}>Expert answers about Ruckus BeamFlex+, Ruckus One, and enterprise Wi-Fi deployments from ALG Data Guard's wireless engineers.</p>
          </div>
          <div className="rk-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`rk-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="rk-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="rk-faq-item__q">{faq.q}</span>
                  <span className="rk-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="rk-faq-item__answer"><div className="rk-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="rk-cta">
        <div className="rk-cta__bg" aria-hidden="true" />
        <div className="rk-cta__overlay" aria-hidden="true" />
        <div className="rk-cta__inner">
          <div className="rk-label rk-label--dark"><span className="rk-dot" /> Get Started</div>
          <h2 className="rk-headline rk-cta__title">
            Build Wireless<br />That <span className="rk-green-text">Never Fails.</span>
          </h2>
          <p className="rk-cta__sub">
            Whether you're deploying Wi-Fi 7 for a dense campus, upgrading a hotel's guest wireless, connecting a sports stadium, or implementing IoT for a healthcare facility — ALG Data Guard's Ruckus-certified wireless engineers design and deliver networks built to perform under pressure.
          </p>
          <div className="rk-cta__actions">
            <button className="rk-btn rk-btn--green" onClick={() => navigate("/contact")}>
              Request a Wireless Assessment <ArrowIcon />
            </button>
            <button className="rk-btn rk-btn--ghost-white" onClick={() => navigate("/contact")}>
              Get a Wi-Fi 7 Quote
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
